import isApiError from "@insite/client-framework/Common/isApiError";
import {
    ApiParameter,
    API_URL_CURRENT_FRAGMENT,
    patch,
    post,
    requestVoid,
    ServiceResult,
} from "@insite/client-framework/Services/ApiService";
import { AddProductApiParameter } from "@insite/client-framework/Services/CartService";
import { CartLineCollectionModel, CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { CartOptimizationResponseModel } from "@store/Types/CartOptimizationResponseModel";

export interface RemoveCartLinesApiParameter extends ApiParameter {
    cartId: string;
    cartLineIds: string[];
}

export interface UpdateCartLinesApiParameter extends ApiParameter {
    cartId: string;
    cartLineCollection: CartLineCollectionModel;
}

export interface OptimizeCartApiParameter extends ApiParameter {
    cartId: string;
}

const cartsUrl = "/api/v1/carts";

/* Poolcorp Custom Code */
export function removeCartLines(parameter: RemoveCartLinesApiParameter) {
    const query = parameter.cartLineIds.map(o => `cartLineIds=${o}`).join("&");
    return requestVoid(`${cartsUrl}/${parameter.cartId}/cartlines/poolcorp/batch?${query}`, "DELETE", {
        "Content-Type": "application/json",
    });
}

export function optimizeCart(parameter: OptimizeCartApiParameter){
    return patch<CartOptimizationResponseModel>(`${cartsUrl}/${parameter.cartId}/cartlines/poolcorp/optimize`, {});
}
/* Poolcorp Custom Code */

export function updateCartLines(parameter: UpdateCartLinesApiParameter) {
    return patch<CartLineCollectionModel>(
        `${cartsUrl}/${parameter.cartId}/cartlines/poolcorp/batch`,
        parameter.cartLineCollection,
    );
}

// Poolcorp Custom Code - Override of addToCart
export function addProduct(parameter: AddProductApiParameter) {
    const cartLine = {
        productId: parameter.productId,
        qtyOrdered: parameter.qtyOrdered,
        unitOfMeasure: parameter.unitOfMeasure,
        notes: parameter.notes,
        sectionOptions: parameter.sectionOptions,
    } as CartLineModel;

    if (parameter.subscription) {
        cartLine.properties = {
            ...cartLine.properties, // Poolcorp Custom Code
            productSubscription: JSON.stringify(parameter.subscription),
        };
    }

    // Poolcorp Custom Code Start
    const additionalQueryStringParameters = parameter.additionalQueryStringParameters;

    if (additionalQueryStringParameters?.requestedFulfillmentMethod) {
        cartLine.properties = {
            ...cartLine.properties,
            requestedFulfillmentMethod: additionalQueryStringParameters.requestedFulfillmentMethod,
        };
    }

    if (additionalQueryStringParameters?.warehouse) {
        cartLine.properties = {
            ...cartLine.properties,
            warehouse: additionalQueryStringParameters.warehouse,
        };
    }
    // Poolcorp Custom Code End

    return post(`${cartsUrl}/${API_URL_CURRENT_FRAGMENT}/cartlines`, cartLine);
}

// Poolcorp Custom Code - Override of addProductWithResult
export async function addProductWithResult(parameter: AddProductApiParameter): Promise<ServiceResult<CartLineModel>> {
    const cartLine = {
        productId: parameter.productId,
        qtyOrdered: parameter.qtyOrdered,
        unitOfMeasure: parameter.unitOfMeasure,
        notes: parameter.notes,
        sectionOptions: parameter.sectionOptions,
    } as CartLineModel;

    if (parameter.subscription) {
        cartLine.properties = {
            ...cartLine.properties, // Poolcorp Custom Code
            productSubscription: JSON.stringify(parameter.subscription),
        };
    }

    // Poolcorp Custom Code Start
    const additionalQueryStringParameters = parameter.additionalQueryStringParameters;

    if (additionalQueryStringParameters?.requestedFulfillmentMethod) {
        cartLine.properties = {
            ...cartLine.properties,
            requestedFulfillmentMethod: additionalQueryStringParameters.requestedFulfillmentMethod,
        };
    }

    if (additionalQueryStringParameters?.warehouse) {
        cartLine.properties = {
            ...cartLine.properties,
            warehouse: additionalQueryStringParameters.warehouse,
        };
    }
    // Poolcorp Custom Code End

    try {
        const cartLineModel = await post(`${cartsUrl}/${API_URL_CURRENT_FRAGMENT}/cartlines`, cartLine);
        return {
            successful: true,
            result: cartLineModel,
        };
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            return {
                successful: false,
                errorMessage: error.errorJson.message,
            };
        }
        if (error.status === 403) {
            return {
                successful: false,
                errorMessage: error.errorMessage,
                statusCode: 403,
            };
        }
        throw error;
    }
}
// Poolcorp Custom Code End